import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { listLatestProducts } from "../../actions/productActions";
import ProductImage from "../../components/images/ProductImage";
import { getProductDisplayPrice } from "../../utils/productPricing";

import "./HomeNewArrivals.css";

function getCategoryLabel(product = {}) {
  if (product.category?.name) return product.category.name;
  if (typeof product.category === "string") return product.category;
  if (product.shopKey) return product.shopKey.replaceAll("-", " ");
  return "Archive";
}

/**
 * Build a product URL that matches the App.js route pattern:
 *   /shop/:shopKey/product/:identifier   (preferred — slug or _id)
 *   /shop/:shopKey                       (fallback — goes to shop listing)
 *
 * The route does NOT support /shop/:shopKey/:slug (no /product/ segment).
 */
function getProductUrl(product = {}) {
  const shopKey = product.shopKey;
  const identifier = product.slug || product._id || product.id;

  if (shopKey && identifier) {
    return `/shop/${shopKey}/product/${identifier}`;
  }

  if (shopKey) {
    return `/shop/${shopKey}`;
  }

  // Last resort — goes to shop landing
  return "/shop";
}

/* ── Skeleton placeholder (loading state) ────────────────── */
function SkeletonCard() {
  return (
    <div className="home-new-arrivals__card home-new-arrivals__card--skeleton" aria-hidden="true">
      <div className="home-new-arrivals__image-wrap home-new-arrivals__skeleton-block" />
      <div className="home-new-arrivals__body home-new-arrivals__body--skeleton">
        <div className="home-new-arrivals__skeleton-line home-new-arrivals__skeleton-line--short" />
        <div className="home-new-arrivals__skeleton-line" />
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */
export default function HomeNewArrivals() {
  const dispatch = useDispatch();

  const productLatest = useSelector((state) => state.productLatest);

  const {
    loading = false,
    error = null,
    products = [],
  } = productLatest || {};

  // Guard: ensure products is always an array even if reducer misbehaves
  const safeProducts = Array.isArray(products) ? products : [];

  useEffect(() => {
    dispatch(listLatestProducts({ limit: 4 }));
  }, [dispatch]);

  return (
    <section
      className="home-new-arrivals"
      aria-labelledby="home-new-arrivals-title"
    >
      <div className="home-new-arrivals__header">
        <div>
          <p className="home-new-arrivals__kicker">Recently added</p>
          <h2 id="home-new-arrivals-title">New pieces from the archive.</h2>
        </div>

        <Link className="home-new-arrivals__view-all" to="/shop">
          View all
        </Link>
      </div>

      {/* Loading skeletons */}
      {loading && (
        <div className="home-new-arrivals__grid" aria-busy="true" aria-label="Loading new arrivals">
          {[0, 1, 2, 3].map((n) => (
            <SkeletonCard key={n} />
          ))}
        </div>
      )}

      {/* Error state — silent fail, never crashes homepage */}
      {error && !loading && (
        <p className="home-new-arrivals__status home-new-arrivals__status--error" role="alert">
          We could not load the latest pieces right now. Please try again later.
        </p>
      )}

      {/* Empty state */}
      {!loading && !error && safeProducts.length === 0 && (
        <p className="home-new-arrivals__status">
          No pieces listed yet —{" "}
          <Link to="/shop" className="home-new-arrivals__status-link">
            browse the shop
          </Link>
          .
        </p>
      )}

      {/* Product grid */}
      {!loading && !error && safeProducts.length > 0 && (
        <div className="home-new-arrivals__grid">
          {safeProducts.map((product) => {
            const displayPrice = getProductDisplayPrice(product);

            return (
              <article
                className="home-new-arrivals__card"
                key={product._id || product.id || product.slug}
              >
                <Link
                  to={getProductUrl(product)}
                  className="home-new-arrivals__link"
                >
                  <div className="home-new-arrivals__image-wrap">
                    <ProductImage
                      product={product}
                      fallback="/images/home/academy-costumes.png"
                      alt={product.name || "Archive piece"}
                      className="home-new-arrivals__image"
                      sizes="(max-width: 640px) calc(100vw - 36px), (max-width: 1040px) calc(50vw - 48px), calc(25vw - 36px)"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <div className="home-new-arrivals__body">
                    <div>
                      <p className="home-new-arrivals__category">
                        {getCategoryLabel(product)}
                      </p>
                      <h3>{product.name}</h3>
                    </div>

                    {displayPrice ? (
                      <p className="home-new-arrivals__price">
                        {displayPrice}
                      </p>
                    ) : null}
                  </div>
                </Link>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
